// Real backend integration for the public (pre-login) PUBLISHED terms list.
// Activated by VITE_USE_MOCK_API=false; see public-terms.adapter.ts.
// Maps the server error envelope { error: { code, message } } to PublishedTermsError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  PublishedTermsError,
  type PublishedTerm,
  type PublishedTermsApi,
  type PublishedTermsErrorCode,
} from '../types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
}

function mapError(e: unknown): never {
  // A guard-thrown typed error must pass through unchanged — otherwise the `!response`
  // branch below would misclassify it as NETWORK.
  if (e instanceof PublishedTermsError) throw e;
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never got an HTTP reply — offline, DNS, or timeout.
  if (!candidate.response) {
    throw new PublishedTermsError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response?.data?.error;
  throw new PublishedTermsError(
    (payload?.code as PublishedTermsErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
  );
}

export const publishedTermsHttpApi: PublishedTermsApi = {
  // Public — no Authorization header, no token. Returns every PUBLISHED term.
  async listPublishedTerms(): Promise<PublishedTerm[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<PublishedTerm[]>>(endpoints.auth.publicTerms);
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new PublishedTermsError('UNKNOWN', '약관을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },
};
